// Brief Phase B2 §4 — Add-to-Calendar popover with double action
//
// Replaces the single "Ajouter au calendrier" button with a popover offering:
//   1) "Ajouter à OpenSapiens" → POST /api/events/{id}/bookmark (event_bookmarks)
//   2) "Exporter (.ics)"       → download a client-built ICS file (Outlook /
//                                 Apple Calendar / Google Calendar compatible)
//
// The popover anchors below the trigger button. Click-outside / blur closes it.
// `triggerClassName` allows host components (Feed cards, EventDetailsModal,
// ProfileWall) to keep their existing visual identity.
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDays, Bookmark, Download, Check } from 'lucide-react';
import { parseUtc } from '../lib/calendarDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, '0');
const fmtIcsDate = (d) =>
  `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
const escIcs = (s = '') =>
  String(s).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');

const buildIcsBlob = ({ eventId, title, description, start, end, visioLink }) => {
  const uid = `${eventId || Math.random().toString(36).slice(2)}@opensapiens.ch`;
  const visio = visioLink || '';
  const descRaw = visio
    ? (description ? `Lien visio: ${visio}\n\n${description}` : `Lien visio: ${visio}`)
    : (description || '');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//OpenSapiens//FR',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${fmtIcsDate(new Date())}`,
    `DTSTART:${fmtIcsDate(start)}`,
    `DTEND:${fmtIcsDate(end)}`,
    `SUMMARY:${escIcs(title)}`,
    `DESCRIPTION:${escIcs(descRaw)}`,
    `LOCATION:${visio}`,
  ];
  if (visio) {
    lines.push(`URL:${visio}`);
    lines.push(`X-MICROSOFT-SKYPETEAMSMEETINGURL:${visio}`);
  }
  lines.push('STATUS:CONFIRMED', 'END:VEVENT', 'END:VCALENDAR');
  const ics = lines.join('\r\n');
  return new Blob([ics], { type: 'text/calendar;charset=utf-8' });
};

const downloadIcs = ({ eventId, title, description, start, end, visioLink }) => {
  const blob = buildIcsBlob({ eventId, title, description, start, end, visioLink });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(title || 'event').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const AddToCalendarPopover = ({
  event,
  source = 'unknown',
  triggerClassName = 'inline-flex items-center gap-1.5 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 px-3 py-1.5 rounded-lg transition-colors',
  testIdPrefix = 'event-add-calendar',
}) => {
  const [open, setOpen] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const eventId = event?.event_id;
  const handleBookmark = async () => {
    if (!eventId || busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/events/${eventId}/bookmark`,
        { source },
        { withCredentials: true },
      );
      setBookmarked(true);
      toast.success('Ajouté à ton OpenSapiens');
      setTimeout(() => setOpen(false), 600);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        toast.error('Connecte-toi pour suivre cet événement');
      } else {
        toast.error(e?.response?.data?.detail || 'Action impossible');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleIcsExport = () => {
    if (!event?.start_time || !event?.end_time) {
      toast.error('Date d\u2019événement manquante');
      return;
    }
    downloadIcs({
      eventId,
      title: event.title,
      description: event.description,
      start: parseUtc(event.start_time),
      end: parseUtc(event.end_time),
      visioLink: event.visio_link,
    });
    toast.success('Fichier .ics téléchargé');
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={triggerClassName}
        data-testid={`${testIdPrefix}-btn`}
      >
        <CalendarDays className="h-3.5 w-3.5" />Ajouter au calendrier
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 z-30 w-60 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg py-1"
          data-testid={`${testIdPrefix}-popover`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={handleBookmark}
            disabled={busy || bookmarked}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-60"
            data-testid={`${testIdPrefix}-bookmark`}
          >
            {bookmarked ? (
              <Check className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <Bookmark className="h-3.5 w-3.5 text-[#7F77DD]" />
            )}
            <span className="flex-1">
              {bookmarked ? 'Ajouté à OpenSapiens' : 'Ajouter à OpenSapiens'}
            </span>
          </button>
          <button
            type="button"
            onClick={handleIcsExport}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
            data-testid={`${testIdPrefix}-ics`}
          >
            <Download className="h-3.5 w-3.5 text-gray-500" />
            <span className="flex-1">Exporter (.ics)</span>
          </button>
          <p className="px-3 pt-1 pb-1.5 text-[10px] text-gray-400">
            Suivi OpenSapiens · ou export Outlook/Google/Apple
          </p>
        </div>
      )}
    </div>
  );
};

export default AddToCalendarPopover;
